/*
  The arithmetic behind fetching a game's whole set catalogue (services/useAllGameSets).

  The catalogue arrives in pages of SETS_PAGE_SIZE because the API caps `limit` there
  (asking for 1000 returns 200). It used to be walked one page at a time, each request
  waiting for the last, which on Pokémon was six round trips back to back (about 4.5s
  from New Zealand) before the game page's Top 10 could show. Page 0 says how many rows
  there are, so every later offset is known the moment it lands and they can all be
  asked for at once.
*/

export const SETS_PAGE_SIZE = 200;

/* Every offset after the first page, for a catalogue of `total` rows. */
export const remainingPageOffsets = (total: number, pageSize: number = SETS_PAGE_SIZE): number[] => {
  const offsets: number[] = [];
  for (let offset = pageSize; offset < total; offset += pageSize) {
    offsets.push(offset);
  }
  return offsets;
};

/*
  The pages in hand, joined in page order whatever order they landed in, and whether that
  is all of them. A page that has not arrived is skipped rather than waited for, so the
  list grows as pages land; `complete` is what says it is the whole catalogue.
*/
export const assemblePages = <T>(
  pages: Readonly<Record<number, readonly T[]>>,
  total: number | undefined,
  pageSize: number = SETS_PAGE_SIZE
): { rows: T[]; complete: boolean } => {
  if (total === undefined) {
    return { rows: [], complete: false };
  }
  const rows: T[] = [];
  let complete = true;
  for (let offset = 0; offset < Math.max(total, 1); offset += pageSize) {
    const page = pages[offset];
    if (page) {
      rows.push(...page);
    } else {
      complete = false;
    }
  }
  return { rows, complete };
};

/*
  True when the rows run from most graded to least. The sets endpoint returns them that
  way by default (checked on 2026-10-20 across all 214 games, archived rows and ties
  included), and that order is what lets the game page rank its Top 10 off page 0 instead
  of waiting for the last page. Checked on the payload rather than assumed, so a backend
  that changed its default order would send the shelf back to waiting, not to wrong.
*/
export const isRankedByCardsGraded = (
  rows: readonly { cardsGraded?: number | null }[]
): boolean => {
  for (let index = 1; index < rows.length; index += 1) {
    if ((rows[index - 1].cardsGraded ?? 0) < (rows[index].cardsGraded ?? 0)) {
      return false;
    }
  }
  return true;
};
